import { apiFetch } from '@/api/client';
import type { Alert, AlertSeverity, AlertType } from '@/types';

interface AlertListOptions {
  unread?: boolean;
  severity?: AlertSeverity;
  includeGlobal?: boolean;
  type?: AlertType;
  q?: string;
  sort?: 'date' | 'severity';
  order?: 'asc' | 'desc';
}

export const alerts = {
  list: (siteId: string, opts: AlertListOptions = {}) => {
    const params = new URLSearchParams({ site_id: siteId });
    if (opts.unread) params.set('unread', 'true');
    if (opts.severity) params.set('severity', opts.severity);
    if (opts.includeGlobal === false) params.set('include_global', 'false');
    if (opts.type) params.set('type', opts.type);
    if (opts.q) params.set('q', opts.q);
    if (opts.sort) params.set('sort', opts.sort);
    if (opts.order) params.set('order', opts.order);
    return apiFetch<Alert[]>(`/alerts/?${params.toString()}`);
  },
  // listGlobal returns only global (NULL site_id) alerts. Used by SitesPage to
  // surface scaffold_failed alerts (which are global), and by AlertsPage on the
  // global /alerts route — where there is no current site to scope by.
  listGlobal: (opts: Omit<AlertListOptions, 'includeGlobal'> = {}) => {
    const params = new URLSearchParams({ include_global: 'true' });
    if (opts.unread) params.set('unread', 'true');
    if (opts.severity) params.set('severity', opts.severity);
    if (opts.type) params.set('type', opts.type);
    if (opts.q) params.set('q', opts.q);
    if (opts.sort) params.set('sort', opts.sort);
    if (opts.order) params.set('order', opts.order);
    return apiFetch<Alert[]>(`/alerts/?${params.toString()}`);
  },
  markRead: (id: string) =>
    apiFetch<void>(`/alerts/${encodeURIComponent(id)}/read`, {
      method: 'PATCH',
    }),
  // dismissAll / markAll operate across EVERY site (and globals). They back the
  // global /alerts view's Dismiss-read / Mark-all-read controls, where there is
  // no single site_id to scope by. The site-scoped counterparts are
  // dismissRead / markAllRead below.
  dismissAll: () =>
    apiFetch<void>('/alerts/dismiss-all', {
      method: 'POST',
    }),
  markAll: () =>
    apiFetch<{ updated: number }>('/alerts/mark-all', {
      method: 'POST',
    }),
  remove: (id: string) =>
    apiFetch<void>(`/alerts/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  dismissRead: (siteId: string, includeGlobal: boolean = true) => {
    const params = new URLSearchParams({ site_id: siteId });
    if (!includeGlobal) params.set('include_global', 'false');
    return apiFetch<{ deleted: number }>(
      `/alerts/dismiss-read?${params.toString()}`,
      { method: 'POST' },
    );
  },
  markAllRead: (siteId: string, includeGlobal: boolean = true) => {
    const params = new URLSearchParams({ site_id: siteId });
    if (!includeGlobal) params.set('include_global', 'false');
    return apiFetch<{ updated: number }>(
      `/alerts/mark-all-read?${params.toString()}`,
      { method: 'POST' },
    );
  },
  bulkDelete: (ids: string[]) =>
    apiFetch<{ deleted: number }>(`/alerts/bulk`, {
      method: 'POST',
      body: JSON.stringify({ action: 'delete', ids }),
    }),
};
